/* RESPONSIBLE TEAM: team-reporting */

export type ChartPeriod = 'current' | 'previous';

export interface ChartTimeRange {
  start: number;
  end: number;
  timezone?: string;
}

export type ChartDataResponse = ChartData[];

export interface ChartData {
  period: ChartPeriod;
  data: FlatData | GroupedData;
  metadata: ChartMetadata;
}

export interface ChartDataParams {
  metrics: {
    metric_id: string;
    aggregation: { type: string; value?: number; ranges?: unknown[] };
    filter?: Record<string, unknown>;
  }[];
  view_by?: Record<string, unknown>;
  segment_by?: Record<string, unknown>;
  time?: ChartTimeRange;
  filters?: Record<string, unknown>;
  show_other?: boolean;
  dimension_format?: string;
}

export interface ChartMetadata {
  columns: string[];
  summaryRow: SummaryRow | null;
  metricMetadata: MetricMetadata[];
}

export interface MetricMetadata {
  metricId: string;
  improvementDirection: 'increasing' | 'decreasing';
  unit: MetricUnit;
  fractionUnit?: FractionUnit;
}

export type MetricUnit = 'value' | 'percent' | 'seconds' | 'minutes' | 'hours' | 'ratio' | 'score';

export interface FractionUnit {
  numerator: MetricUnit;
  denominator: MetricUnit;
}

export type SummaryCellValue = null | number | DualQueryCell | DimensionCell;
export type FlatSummaryRow = SummaryCellValue[];
export type GroupedSummaryRow = SummaryCellValue[][];
export type SummaryRow = FlatSummaryRow | GroupedSummaryRow;

export type DataRow = Cell[];

// Cell types - discriminated by shape

export type Cell = PrimitiveCell | DimensionCell | DualQueryCell;

export type PrimitiveCell = number | string | null;

export interface DimensionCell {
  kind: 'dimension';
  rawValue: string | number;
  displayValue: string;
  resolvedValue?: Record<string, unknown>;
}

export interface DualQueryCell {
  value: number;
  numeratorValue: number;
  denominatorValue: number;
}

// Data shapes - determined by presence of view_by
export type MetricValue = number | DualQueryCell;
export type FlatData = MetricValue[]; // Ungrouped (no view_by): flat array of metric values
export type GroupedData = DataRow[]; // Grouped (with view_by): array of rows

// Config types

export interface ChartConfig {
  visualizationType: VisualizationType;
  title?: string;
  viewBy?: string;
  viewByDisplay?: string;
  viewByTimeInterval?: TimeInterval;
  segmentBy?: string;
  segmentByDisplay?: string;
  segmentByTimeInterval?: TimeInterval;
  stacked?: boolean;
  chartSeries?: ChartSeries[];
  visualizationOptions?: VisualizationOptions;
  /** Whether to render the chart visualization. When false, the data is available but no chart is shown. */
  renderVisualization?: boolean;
  /** Compact mode: hides axis labels/gridlines, tightens spacing, and uses a simplified tooltip. */
  compact?: boolean;
}

export type VisualizationType =
  | 'bar'
  | 'horizontal-bar'
  | 'line'
  | 'area'
  | 'column-line'
  | 'counter'
  | 'donut'
  | 'heatmap'
  | 'tabular'
  | 'data-table'
  | 'banner';

export type TimeInterval = 'hour' | 'day' | 'week' | 'month' | 'day_of_week' | 'hour_of_day';

export interface ChartSeries {
  metricId: string;
  metricDisplayName?: string;
  aggregation?: string;
  label?: string;
  showDataLabels?: boolean;
}

export interface VisualizationOptions {
  showRelativeValues?: boolean;
  showSummaryRow?: boolean;
  showTimeComparison?: boolean;

  target?: { value: number };
}

export interface RenderContext {
  timezone: string;
  locale: string;
  dateRange?: DateRange;
  height?: number;
  labels?: {
    previousPeriod?: string;
  };
}

export interface DateRange {
  current: { start: Date; end: Date };
  previous?: { start: Date; end: Date };
}

// Type guards

export function isDimensionCell(cell: Cell): cell is DimensionCell {
  return cell !== null && typeof cell === 'object' && 'kind' in cell;
}

export function isDualQueryCell(cell: Cell): cell is DualQueryCell {
  return cell !== null && typeof cell === 'object' && 'numeratorValue' in cell;
}

export function isGroupedData(data: FlatData | GroupedData): data is GroupedData {
  return data.length > 0 && Array.isArray(data[0]);
}

export function isGroupedSummaryRow(row: SummaryRow): row is GroupedSummaryRow {
  return row.length > 0 && Array.isArray(row[0]);
}

export function isMetricValueDualQuery(value: MetricValue): value is DualQueryCell {
  return typeof value === 'object' && 'numeratorValue' in value;
}
